import React, { useEffect, useState } from 'react';
import { Building2, CheckCircle2, Shield, FileCheck } from 'lucide-react';

export const GovernmentProjects = () => {
  const [governmentProjects, setGovernmentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/government-projects`);
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        const data = await response.json();
        setGovernmentProjects(data);
      } catch (err) {
        console.error('Error fetching government projects:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProjects();
  }, [BACKEND_URL]);
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center px-4 py-2 bg-slate-100 rounded-full text-sm font-medium text-slate-700 mb-6">
              <Shield className="w-4 h-4 mr-2" />
              Trusted by Government Bodies
            </div>
            <h1 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Government & Infrastructure Waterproofing
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Delivering compliance-grade waterproofing solutions for public infrastructure with proven durability and inspection standards.
            </p>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Why Government Bodies Trust Us
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Compliance & Standards</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                All work meets government inspection standards and regulatory requirements for public infrastructure.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <Building2 className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Scale of Execution</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Proven capability to handle large-scale projects with multiple work sites and strict timelines.
              </p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm border border-slate-200">
              <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center mb-4">
                <FileCheck className="w-6 h-6 text-slate-700" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Documentation & Warranty</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Complete documentation, material certifications, and long-term warranty support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Projects Showcase */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-4">
              Recent Government Projects
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Sample projects completed with satisfactory ratings from government authorities.
            </p>
          </div>
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading projects...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600">Failed to load projects. Please try again later.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {governmentProjects.map((project) => (
                <div key={project.id} className="bg-white rounded-lg border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative h-48 bg-slate-100 overflow-hidden">
                    {project.imageUrl ? (
                      <img 
                        src={project.imageUrl} 
                        alt={project.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex items-center justify-center h-full">
                        <div className="text-center text-slate-400">
                          <Building2 className="w-16 h-16 mx-auto mb-2" />
                          <p className="text-sm">Project Image</p>
                        </div>
                      </div>
                    )}
                    {project.status && (
                      <div className="absolute top-3 right-3">
                        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                          project.status === 'Completed' ? 'bg-green-100 text-green-800' :
                          project.status === 'Ongoing' ? 'bg-blue-100 text-blue-800' :
                          'bg-amber-100 text-amber-800'
                        }`}>
                          {project.status}
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-sm text-slate-500 mb-2">{project.year}</div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-3">{project.title}</h3>
                    <p className="text-sm text-slate-600 mb-4">{project.description}</p>
                    <div className="flex items-center space-x-2">
                      <CheckCircle2 className="w-4 h-4 text-slate-700" />
                      <span className="text-sm font-medium text-slate-700">{project.area}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="py-16 lg:py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-900 mb-6 text-center">
              Our Infrastructure Capabilities
            </h2>
            <div className="space-y-4">
              {[
                'Water treatment and storage facilities',
                'Public housing and residential complexes',
                'Government office buildings and institutions',
                'Hospitals and healthcare facilities',
                'Educational institutions and schools',
                'Underground structures and basements',
                'Bridges and elevated structures',
                'Municipal infrastructure projects'
              ].map((capability, index) => (
                <div key={index} className="flex items-start space-x-3 bg-white rounded-lg p-4 border border-slate-200">
                  <CheckCircle2 className="w-5 h-5 text-slate-700 mt-0.5 flex-shrink-0" />
                  <span className="text-base text-slate-700">{capability}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-slate-800 py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Partner with Us for Your Infrastructure Project
          </h2>
          <p className="text-lg text-slate-300 mb-8">
            Contact us to discuss compliance requirements and project specifications.
          </p>
          <a
            href="/contact"
            className="inline-flex items-center px-8 py-4 bg-white text-slate-800 rounded-lg text-base font-semibold hover:bg-slate-100 transition-colors"
          >
            Request Project Consultation
          </a>
        </div>
      </section>
    </div>
  );
};